import Dinero, { DineroObject } from 'dinero.js';
import Analytics from '@monolith/legacy/services/analytics';
import { Checkout, Item } from '@monolith/legacy/types/cart';
import Product from '@monolith/legacy/types/product';
import { CartViewedEvent2 } from '../events/cart-viewed.event';
import { OrderCompletedEvent } from '@monolith/legacy/services/analytics/events/order-completed.event';
import { ProductListViewedEvent } from '@monolith/legacy/services/analytics/events/product-list-viewed.event';
import { ProductViewedComponent, ProductViewedEvent } from '@monolith/legacy/services/analytics/events/product-viewed.event';
import { Order as SegmentOrder, Product as SegmentProduct } from '../events/segment-ecommerce-types';
import { CartPreviewViewedEvent } from '@monolith/legacy/services/analytics/events/cart-preview-viewed.event';
import UserClick from '@monolith/legacy/services/analytics/events/user-click.event';

export interface TrackProductListViewedParams {
  listId: string;
  category: string;
  products: SegmentProduct[];
}

export const trackProductListViewed = ({ listId, category, products }: TrackProductListViewedParams) =>
  Analytics.track(
    new ProductListViewedEvent({
      list_id: listId,
      category,
      products,
    })
  );

const mapToProduct = (product: Product) => ({
  product_id: product.id,
  sku: `${product.id}`,
  category: product.categories?.map(({ id }) => id).join(','),
  name: product.name,
  brand: product.brand?.name,
  price: Dinero(product.original_wholesale_price as DineroObject).toUnit(),
  url: product.link,
  image_url: product.images?.[0],
  availability: null,
});

const mapFromCartItemToProduct = (brandName: string) => (item: Item) => ({
  ...mapToProduct(item.product),
  brand: brandName,
  quantity: item.multiplied_quantity,
  variant: item.option?.name,
});

/**
 * `component` is required so the drawer and the full product page stay distinguishable:
 * both are rendered by the same component, and a missing value would silently collapse
 * them back together, leaving drawer-scoped view rates without a denominator.
 */
export const trackProductViewed = (product: Product, component: ProductViewedComponent) =>
  Analytics.track(new ProductViewedEvent({ ...mapToProduct(product), component }));

/**
 * track cart page release 2 loading time in miliseconds
 */
export const trackCartViewed2Ms = (cartId: string, brands: object[], cartLoadingTime: number) => {
  Analytics.track(
    new CartViewedEvent2({
      tracking_version: '2.0',
      cart_id: cartId,
      brands,
      loading_time: cartLoadingTime,
    })
  );
};

export const trackCartPreviewViewed = (currentUserId: string, brands: object[]) => {
  Analytics.track(
    new CartPreviewViewedEvent({
      id_user: currentUserId,
      brands,
    })
  );
};

export const trackGoToBrandPageCartPreview= (brandUuid: string) => {
  Analytics.track(
    new UserClick({
      component: 'cart_preview',
      action: 'click_brand',
      uuid_brand: brandUuid,
    })
  );
};

export const trackGoToCartCartPreview= () => {
  Analytics.track(
    new UserClick({
      component: 'cart_preview',
      action: 'go_to_cart',
    })
  );
};

export const cartIsEligibleForCheckout = (cart) => {
  return cart.minimum.above === true && cart.saved_for_later === false;
};

const amountToDecimal = (amount: number): number => {
  return parseFloat((amount / 100).toFixed(2));
};

export const trackOrderCompleted = (checkout: Checkout) => {
  const orders: SegmentOrder[] = checkout.carts.filter(cartIsEligibleForCheckout).map((cart) => ({
    order_id: `${cart.id}`,
    subtotal: amountToDecimal(cart.total_amount.amount),
    total: amountToDecimal(cart.total_amount_with_vat?.amount),
    tax: amountToDecimal(cart.total_amount_vat?.amount),
    currency: cart.total_amount?.currency,
    products: cart.items.map(mapFromCartItemToProduct(cart.brand?.name)),
    is_first_order_on_this_brand: cart.is_first_order_on_this_brand,
  }));

  if (orders.length === 0) {
    return;
  }

  Analytics.track(
    new OrderCompletedEvent({
      checkout_id: `${checkout.id}`,
      orders,
      total_amount: amountToDecimal(checkout.total_amount.amount),
      total_amount_vat: amountToDecimal(checkout.total_amount_vat.amount),
      total_amount_with_vat: amountToDecimal(checkout.total_amount_with_vat.amount),
      is_first_checkout: checkout.is_first_checkout,
    })
  );
};
